import type { RecordingMeta } from '@/lib/recordings/types/types';
import type { CapturedExcerpt, TemplateSourceRequest } from '@/lib/templates/api';
import { surfaceDisplayStatus, type SurfaceEntry, type SurfaceSource } from '@/lib/sync/document-surfaces';
import type { EventPreference, TemplateSourceKind } from '@/lib/templates/catalog';
import { formatEventTime } from '@/lib/calendar/format-event-time';
import type { LinkedEvent } from '@/lib/calendar/use-document-events';

export type TemplateSourceOptionKind = 'page' | 'document' | 'surface' | 'recording' | 'passage' | 'event';

export interface TemplateSourceOption {
  id: string;
  kind: TemplateSourceOptionKind;
  name: string;
  kindLabel: string;
  request: TemplateSourceRequest;
  onScreen?: boolean;
  finishedAt?: string;
  startAt?: string;
  endAt?: string;
  sourceTitle?: string;
  preview?: string;
  /** A page with no body text: listed, never ticked for the person. */
  empty?: boolean;
  /** A meeting that may be ticked by default: linked by an editor, and not a
   *  recurring series (whose start does not say when the next one is). */
  defaultable?: boolean;
}

/** Mirrors the server's per-run source cap (routes/templates.rs `accepts_sources`). */
export const MAX_TICKED_SOURCES = 5;

function finishedTime(option: TemplateSourceOption): number {
  return option.finishedAt ? Date.parse(option.finishedAt) : 0;
}

export function pageSourceOption(entityId: string, title: string, hasText = true): TemplateSourceOption {
  return {
    id: `page:${entityId}`,
    kind: 'page',
    name: title,
    kindLabel: 'the page itself',
    request: { kind: 'document', entityId },
    ...(hasText ? {} : { empty: true }),
  };
}

export function documentSourceOption({ id, title }: { id: string; title: string }): TemplateSourceOption {
  return {
    id: `document:${id}`,
    kind: 'document',
    name: title,
    kindLabel: 'document',
    request: { kind: 'document', entityId: id },
  };
}

/** The other whole documents a failed run read, for its Try again. */
export function addedDocumentsFrom(
  options: readonly TemplateSourceOption[],
  preferred: readonly SurfaceSource[] | null,
): TemplateSourceOption[] {
  const offered = new Set(options.map((o) => o.request.kind === 'document' ? o.request.entityId : null));
  const added: TemplateSourceOption[] = [];
  for (const source of preferred ?? []) {
    if (source.kind !== 'document' || source.excerpt || !source.entityId || offered.has(source.entityId)) continue;
    offered.add(source.entityId);
    added.push(documentSourceOption({ id: source.entityId, title: source.title }));
  }
  return added;
}

export function passageSourceOption({ entityId, surfaceId, sourceTitle, excerpt }: {
  entityId: string;
  surfaceId: string | null;
  sourceTitle: string;
  excerpt: CapturedExcerpt;
}): TemplateSourceOption {
  const scope = { kind: 'excerpt' as const, excerptId: excerpt.excerptId };
  return {
    id: 'passage',
    kind: 'passage',
    name: 'Selected passage',
    kindLabel: 'passage',
    request: surfaceId === null
      ? { kind: 'document', entityId, scope }
      : { kind: 'surface', entityId, surfaceId, scope },
    sourceTitle,
    preview: excerpt.preview,
  };
}

/** The name an audio surface is created with: not a name the person chose. */
export const RECORDING_SURFACE_TITLE = 'Recording';

/** "Today, 3:05 PM" / "Sep 1, 9:30 AM": two recordings on one day differ. */
export function recordingTimeLabel(iso: string): string {
  return formatEventTime({ startAt: iso }).replace(' · ', ', ');
}

function recordingName(
  recording: RecordingMeta,
  entries: readonly SurfaceEntry[],
  formatTime: (iso: string) => string,
): string {
  const audio = entries.find((e) => e.generator === 'recording' && e.recordingId === recording.id);
  const title = audio?.title.trim();
  return title && title !== RECORDING_SURFACE_TITLE
    ? title
    : `${RECORDING_SURFACE_TITLE} · ${formatTime(recording.startedAt)}`;
}

export function recordingSourceOptions(
  recordings: readonly RecordingMeta[],
  entries: readonly SurfaceEntry[],
  formatTime: (iso: string) => string,
): TemplateSourceOption[] {
  return recordings
    .map((r): TemplateSourceOption => ({
      id: `recording:${r.id}`,
      kind: 'recording',
      name: recordingName(r, entries, formatTime),
      kindLabel: 'recording',
      request: { kind: 'transcript', recordingId: r.id },
      finishedAt: r.endedAt ?? r.startedAt,
    }))
    .sort((a, b) => finishedTime(b) - finishedTime(a));
}

export function isReadableTextSurface(entry: SurfaceEntry): boolean {
  const status = surfaceDisplayStatus(entry);
  return entry.surface === 'text'
    && (entry.source === 'owned' || entry.source === 'generated')
    && status !== 'generating' && status !== 'failed';
}

function surfaceSourceOption(entityId: string, entry: SurfaceEntry, onScreen: boolean): TemplateSourceOption {
  return {
    id: `surface:${entry.id}`,
    kind: 'surface',
    name: entry.title,
    kindLabel: onScreen ? 'this surface' : 'surface',
    request: { kind: 'surface', entityId, surfaceId: entry.id, scope: { kind: 'full' } },
    onScreen,
  };
}

export interface SourceOptionFacts {
  entityId: string;
  title: string;
  hasBodyText: boolean;
  entries: readonly SurfaceEntry[];
  activeId: string | null;
  recordings: readonly RecordingMeta[];
  events?: readonly LinkedEvent[];
  formatRecordingTime: (iso: string) => string;
}

/** One option per source of each accepted kind (ui-design.md §3.2.1), the
 *  surface on screen first. Canvas, audio and linked surfaces never appear. */
export function sourceOptionsFor(
  kinds: readonly TemplateSourceKind[],
  facts: SourceOptionFacts,
): TemplateSourceOption[] {
  const { entityId, entries, activeId } = facts;
  const surfaces = kinds.includes('surface') ? entries.filter(isReadableTextSurface) : [];
  const onScreen = surfaces.find((e) => e.id === activeId);
  return [
    ...(kinds.includes('calendar_event')
      ? eventSourceOptions(entityId, facts.events ?? [], facts.formatRecordingTime)
      : []),
    ...(onScreen ? [surfaceSourceOption(entityId, onScreen, true)] : []),
    ...(kinds.includes('document') ? [pageSourceOption(entityId, facts.title, facts.hasBodyText)] : []),
    ...surfaces.filter((e) => e !== onScreen).map((e) => surfaceSourceOption(entityId, e, false)),
    ...(kinds.includes('transcript')
      ? recordingSourceOptions(facts.recordings, entries, facts.formatRecordingTime)
      : []),
  ];
}

/** Ticked for the person, in order: the passage, the surface on screen, the
 *  page when it has text, the newest recording, then the first other source.
 *  A page with no text is never ticked. */
export function bestMatchSourceId(options: readonly TemplateSourceOption[]): string | null {
  const passage = options.find((o) => o.kind === 'passage');
  if (passage) return passage.id;
  const onScreen = options.find((o) => o.kind === 'surface' && o.onScreen);
  if (onScreen) return onScreen.id;
  const page = options.find((o) => o.kind === 'page' && !o.empty);
  if (page) return page.id;
  const newest = options
    .filter((o) => o.kind === 'recording')
    .reduce<TemplateSourceOption | null>(
      (best, o) => (best === null || finishedTime(o) > finishedTime(best) ? o : best),
      null,
    );
  return newest?.id ?? options.find((o) => !o.empty)?.id ?? null;
}

function sourceOptionId(source: SurfaceSource): string | null {
  if (source.excerpt) return null;
  switch (source.kind) {
    case 'document':
      return source.entityId ? `page:${source.entityId}` : null;
    case 'transcript':
      return source.recordingId ? `recording:${source.recordingId}` : null;
    case 'surface':
      return source.surfaceId ? `surface:${source.surfaceId}` : null;
    case 'calendar_event':
      return source.eventId ? `event:${source.eventId}` : null;
  }
}

const startOf = (o: TemplateSourceOption): number => (o.startAt ? Date.parse(o.startAt) : 0);
const endOf = (o: TemplateSourceOption): number => (o.endAt ? Date.parse(o.endAt) : startOf(o));

/** Pre-meeting (`upcoming`): the meeting in progress, else the soonest to
 *  come, else the most recent past. Follow-up (`started`): the most recent
 *  meeting that has started, else none. Only a meeting that may be ticked by
 *  default counts (`defaultable`). */
export function preferredEvent(
  options: readonly TemplateSourceOption[],
  preference: EventPreference,
  now: number = Date.now(),
): TemplateSourceOption | null {
  const events = options.filter((o) => o.kind === 'event' && o.defaultable);
  const latest = (list: TemplateSourceOption[]) =>
    list.reduce<TemplateSourceOption | null>((best, o) => (best === null || startOf(o) > startOf(best) ? o : best), null);
  const started = events.filter((o) => startOf(o) <= now);
  if (preference === 'started') return latest(started);
  const inProgress = started.find((o) => now < endOf(o));
  if (inProgress) return inProgress;
  const soonest = events
    .filter((o) => startOf(o) > now)
    .reduce<TemplateSourceOption | null>((best, o) => (best === null || startOf(o) < startOf(best) ? o : best), null);
  return soonest ?? latest(started);
}

/** One preferred meeting plus the best other source; the meeting alone only
 *  when nothing else can be ticked. */
export function defaultTicks(
  options: readonly TemplateSourceOption[],
  preference: EventPreference,
  now: number = Date.now(),
): string[] {
  const event = preferredEvent(options, preference, now);
  const best = bestMatchSourceId(options.filter((o) => o.kind !== 'event'));
  return [event?.id, best].filter((id): id is string => typeof id === 'string');
}

/** The preferred sources that are still options, else the default ticks:
 *  the best match, with a meeting beside it for a template that reads one. */
export function initialTicks(
  options: readonly TemplateSourceOption[],
  preferred: readonly SurfaceSource[] | null,
  preference: EventPreference = 'upcoming',
  now: number = Date.now(),
): string[] {
  const wanted = new Set((preferred ?? []).flatMap((source) => {
    const id = sourceOptionId(source);
    return id?.startsWith('page:') ? [id, id.replace('page:', 'document:')] : [id];
  }));
  const kept = options.filter((o) => wanted.has(o.id)).map((o) => o.id);
  if (kept.length > 0) return kept;
  return defaultTicks(options, preference, now);
}

export function toggleSource(
  ticked: readonly string[],
  id: string,
  max: number = MAX_TICKED_SOURCES,
): string[] {
  if (ticked.includes(id)) return ticked.filter((t) => t !== id);
  if (ticked.length >= max) return [...ticked];
  return [...ticked, id];
}

/** One option per linked meeting, in the order the server lists them. The
 *  recorded title is the meeting's own, never its time. */
export function eventSourceOptions(
  entityId: string,
  events: readonly LinkedEvent[],
  formatTime: (iso: string) => string,
): TemplateSourceOption[] {
  return events.map(({ eventId, title, startAt, endAt, isRecurringSeries, linkedByEditor }) => ({
    id: `event:${eventId}`,
    kind: 'event',
    name: `${title} · ${isRecurringSeries ? 'recurring' : formatTime(startAt)}`,
    kindLabel: 'meeting',
    request: { kind: 'calendar_event', eventId, entityId },
    sourceTitle: title,
    startAt,
    endAt,
    defaultable: linkedByEditor && !isRecurringSeries,
  }));
}

export function recordedSource({ request, name, sourceTitle }: TemplateSourceOption): SurfaceSource {
  const title = sourceTitle ?? name;
  const excerpt = 'scope' in request && request.scope?.kind === 'excerpt';
  if (request.kind === 'transcript') return { ...request, title };
  if (request.kind === 'calendar_event') return { ...request, title };
  const { scope: _scope, ...ids } = request;
  return { ...ids, title, ...(excerpt ? { excerpt: true as const } : {}) };
}
